import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip } from '@mui/material';
import { CheckCircle, Edit } from '@mui/icons-material';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
  source?: 'employee' | 'relatedRecord';
}

interface EmployeeAutofillSummaryProps {
  fields: PrefilledField[];
  employeeName?: string;
  compact?: boolean;
}

function formatFieldLabel(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/_/g, ' ')
    .trim();
}

function formatValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function hasBeenModified(sourceValue: unknown, currentValue: unknown): boolean {
  if (sourceValue === currentValue) return false;
  if (typeof sourceValue === 'string' && typeof currentValue === 'string') {
    return sourceValue.trim() !== currentValue.trim();
  }
  return true;
}

export function EmployeeAutofillSummary({ fields, employeeName, compact = false }: EmployeeAutofillSummaryProps) {
  if (fields.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {fields.map((field, index) => {
          const modified = hasBeenModified(field.sourceValue, field.currentValue);
          return (
            <Box key={field.field} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {modified ? (
                <Edit sx={{ fontSize: 16, color: 'warning.main' }} />
              ) : (
                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                {formatFieldLabel(field.label)}: {formatValue(field.currentValue)}
              </Typography>
              {index < fields.length - 1 && (
                <Typography variant="body2" color="text.disabled">•</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            📋 Employee data pre-filled from employee record
          </Typography>
          {employeeName && (
            <Typography variant="body2" color="text.secondary">
              — {employeeName}
            </Typography>
          )}
        </Box>
      </CardContent>
      <Table size="small" sx={{ '& td, & th': { border: 0 } }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Pre-filled</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Current</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field) => {
            const modified = hasBeenModified(field.sourceValue, field.currentValue);
            const displayLabel = field.label || formatFieldLabel(field.field);
            return (
              <TableRow key={field.field} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2">{displayLabel}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatValue(field.sourceValue)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {modified ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                        {formatValue(field.sourceValue)}
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        → {formatValue(field.currentValue)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      {formatValue(field.currentValue)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {modified ? (
                    <Chip
                      icon={<Edit />}
                      label="Modified"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  ) : (
                    <Chip
                      icon={<CheckCircle />}
                      label="Confirmed"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}